import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { DiaryManagementNavTabs } from "@/features/admin/components/DiaryManagementNavTabs";
import type { AdminDiaryFilters } from "@/features/admin/diaries/api/adminDiariesApi";
import { useAdminDiaries } from "@/features/admin/diaries/api/adminDiariesApi";
import {
    ADMIN_DIARIES_BROWSE_COLUMNS,
    AdminDiariesBrowseTable,
} from "@/features/admin/diaries/components/AdminDiariesBrowseTable";
import type { AdminDiariesFilterValues } from "@/features/admin/diaries/components/AdminDiariesFilterMenu";
import { emptyAdminDiariesFilterValues } from "@/features/admin/diaries/components/AdminDiariesFilterMenu";
import { AdminDiariesToolbar } from "@/features/admin/diaries/components/AdminDiariesToolbar";
import {
    ADMIN_TABLE_PAGE_SIZE,
    AdminColumnsDialog,
    AdminTablePagination,
    useDebouncedValue,
} from "@/features/admin/tables";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const DiariesSearchZ = z.object({
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    labels: z.union([z.string(), z.array(z.string())]).optional(),
    organisations: z.union([z.string(), z.array(z.string())]).optional(),
    clinicians: z.union([z.string(), z.array(z.string())]).optional(),
    status: z.enum(["all", "locked", "unlocked"]).optional(),
});

function asStringArray(value: string | string[] | undefined): string[] {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
}

export const Route = createFileRoute("/_app/admin/diaries/")({
    component: AdminDiariesPage,
    validateSearch: (search) => DiariesSearchZ.parse(search),
});

function AdminDiariesPage() {
    useNav([
        { label: "Admin", route: { to: "/admin" } },
        { label: "Diaries", route: { to: "/admin/diaries" } },
    ]);

    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const [searchText, setSearchText] = useState(search.q ?? "");
    const debouncedSearch = useDebouncedValue(searchText.trim(), 300);

    const [filterValues, setFilterValues] = useState<AdminDiariesFilterValues>(() => ({
        ...emptyAdminDiariesFilterValues,
        labels: asStringArray(search.labels),
        organisations: asStringArray(search.organisations),
        clinicians: asStringArray(search.clinicians),
        status: search.status ?? "all",
    }));

    const [visibleKeys, setVisibleKeys] = useState(() =>
        ADMIN_DIARIES_BROWSE_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
    );

    const page = search.page ?? 1;

    useEffect(() => {
        const urlQ = search.q ?? "";
        if (debouncedSearch === urlQ) return;
        void navigate({
            search: (prev) => ({
                ...prev,
                q: debouncedSearch || undefined,
                page: 1,
            }),
            replace: true,
        });
    }, [debouncedSearch, navigate, search.q]);

    const filters: AdminDiaryFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            labels: filterValues.labels.length ? filterValues.labels : undefined,
            clinicians: filterValues.clinicians.length ? filterValues.clinicians : undefined,
            organisations: filterValues.organisations.length ? filterValues.organisations : undefined,
            status: filterValues.status,
            page,
            pageSize: ADMIN_TABLE_PAGE_SIZE,
        }),
        [debouncedSearch, filterValues, page]
    );

    const { data, isLoading, isFetching, error } = useAdminDiaries(filters);
    const diaries = data?.diaries ?? [];

    return (
        <AdminPageLayout
            title="Diaries"
            description="Browse system diaries with filters, copy IDs, and links to events and trackers."
        >
            <div className="space-y-4">
                <DiaryManagementNavTabs />

                <AdminDiariesToolbar
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    filterValues={filterValues}
                    onApplyFilters={(values) => {
                        setFilterValues(values);
                        void navigate({
                            search: (prev) => ({
                                ...prev,
                                page: 1,
                                labels: values.labels.length ? values.labels : undefined,
                                organisations: values.organisations.length ? values.organisations : undefined,
                                clinicians: values.clinicians.length ? values.clinicians : undefined,
                                status: values.status === "all" ? undefined : values.status,
                            }),
                            replace: true,
                        });
                    }}
                    isApplying={isFetching}
                    showLockStatus
                >
                    <AdminColumnsDialog
                        columns={ADMIN_DIARIES_BROWSE_COLUMNS}
                        visibleKeys={visibleKeys}
                        onSave={setVisibleKeys}
                    />
                </AdminDiariesToolbar>

                {error && (
                    <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                        Error loading diaries: {error instanceof Error ? error.message : "Unknown error"}
                    </div>
                )}

                {isLoading ? (
                    <ui.Skeleton className="w-full h-64" />
                ) : (
                    !error && (
                        <div className={isFetching ? "opacity-70 transition-opacity" : undefined}>
                            <AdminDiariesBrowseTable diaries={diaries} visibleKeys={visibleKeys} />
                            <div className="mt-3">
                                <AdminTablePagination
                                    page={page}
                                    totalCount={data?.totalCount ?? 0}
                                    pageSize={ADMIN_TABLE_PAGE_SIZE}
                                    isFetching={isFetching}
                                    onPageChange={(nextPage) => {
                                        void navigate({
                                            search: (prev) => ({ ...prev, page: nextPage }),
                                            replace: true,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )
                )}
            </div>
        </AdminPageLayout>
    );
}
